import { TagDto } from "@closerplatform/spinner-openapi";
import { flow, isEmpty, size } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { isSystemTag } from "@fidget/common/models/tags";
import { caseInsensitiveIncludes } from "@fidget/common/utils/searchMatching";
import { useStore } from "../../../../models";
import { usePreloader } from "../../../HOC/withPreloading/withPreloading";
import {
  ListHook,
  ListHookWithFilters,
  ListHookWithSelectables,
  Params
} from "../../models/useSettingsList/types";
import {
  useSettingsList,
  withFilters,
  withSelectables
} from "../../models/useSettingsList/useSettingsList";
import { SettingsForm } from "../../settingsForm";
import { DeleteConfirmation } from "../../settingsForms/deleteConfirmation/deleteConfirmation";
import { ElementFormActions } from "../../settingsForms/elementForm/actions/elementFormActions";
import { ElementForm } from "../../settingsForms/elementForm/elementForm/elementForm";
import { ElementFormCheckbox } from "../../settingsForms/elementForm/icons/elementFormCheckbox";
import { ElementFormDeleteIcon } from "../../settingsForms/elementForm/icons/elementFormDeleteIcon";
import { ElementFormSaveDiscardIcon } from "../../settingsForms/elementForm/icons/elementFormSaveDiscardIcon";
import { BooleanListFilter } from "../../settingsForms/filters/booleanListFilter";
import { ListFilterWrapper } from "../../settingsForms/filters/listFilterWrapper/listFilterWrapper";
import { FilterWrapperProvider } from "../../settingsForms/filters/listFilterWrapper/useFilterWrapper";
import { FormPlaceholder } from "../../settingsForms/fullWidthPlaceholder/formPlaceholder";
import {
  ListBooleanAction,
  ListActions,
  ListActionsSection,
  ListDeleteAction
} from "../../listActions";
import { VerticalSeparator } from "../../settingsForms/separators/verticalSeparator/verticalSeparator";
import { NewTagBox } from "./components/newTagBox/newTagBox";
import { TagBox } from "./components/tagBox/tagBox";
import { ExistingTagFormType, NewTagFormType } from "./types";
import {
  copyExistingTagForm,
  createNewTagTagForm,
  mapExistingFormToUpdateForm,
  mapFormToTag,
  mapNewFormToCreateForm,
  mapTagToForm
} from "./utils";
import { useAgentServices } from "../../../../models/agentServices";

export const TagsSettingsList = observer(() => {
  const {
    newItem,
    handleCreateNewClick,
    discardNewItem,
    saveNewItem,
    items,
    editedItems,
    filteredItems,
    forEachSelected,
    forAllSelected,
    setItemSelected,
    setItemsSelected,
    selectedItems,

    onSubmitAll,
    onDiscardAll,
    apiDeleteItem,
    uiRemoveExistingItem,
    deleteAllSelected
  } = useTagsSettingsState();
  const { uiStore } = useStore();

  const renderNewTagForm = (form: NewTagFormType) => (
    <ElementForm isEdited>
      <NewTagBox form={form} onEnter={saveNewItem} />
      <VerticalSeparator />
      <ElementFormActions>
        <ElementFormSaveDiscardIcon onSave={saveNewItem} onDiscard={discardNewItem} />
      </ElementFormActions>
    </ElementForm>
  );

  const renderNewTagSection = () =>
    newItem ? (
      renderNewTagForm(newItem)
    ) : (
      <FormPlaceholder
        onClick={handleCreateNewClick}
        text={"+ " + intl.formatMessage("settings.tags.addNew")}
      />
    );

  const renderElementForm = (item: ExistingTagFormType, onDeleteClick: () => void) => {
    return (
      <ElementForm>
        <ElementFormCheckbox
          id={item.$.id.$}
          isSelected={!!selectedItems[item.$.id.$]}
          onChange={(val) => setItemSelected(item, val)}
        />
        <VerticalSeparator />
        <TagBox form={item} />
        {!isSystemTag(item.$.tag.$) && (
          <>
            <VerticalSeparator />
            <ElementFormActions>
              <ElementFormDeleteIcon onClick={onDeleteClick} />
            </ElementFormActions>
          </>
        )}
      </ElementForm>
    );
  };

  const renderExistingTagFormTypes = () => (
    <div>
      {filteredItems.map((item) => {
        return (
          <DeleteConfirmation
            key={item.$.id.$}
            areYouSureText={intl.formatMessage("settings.tags.areYouSureToRemove")}
            succesfullyDeletedText={intl.formatMessage("settings.tags.deleteConfirmation")}
            onDelete={() => apiDeleteItem(item.$.id.$)}
            onTimeoutPassed={() => uiRemoveExistingItem(item.$.id.$)}
            onError={() => uiStore.addErrorToast(intl.formatMessage("settings.tags.error.delete"))}
            renderComponent={(onDeleteClick) => renderElementForm(item, onDeleteClick)}
          />
        );
      })}
    </div>
  );

  const renderFilterSection = () => {
    return (
      <FilterWrapperProvider<ExistingTagFormType>>
        <ListFilterWrapper<ExistingTagFormType>
          placeholder={intl.formatMessage("settings.tags.filters.placeholder")}
          allCount={items.length}
          filteredCount={filteredItems.length}
          inputPredicateId="TagInputFilter"
          inputPredicate={(item, input) => caseInsensitiveIncludes(item.$.tag.$, input)}
          headerText={intl.formatMessage("settings.tags.filters.header")}
        >
          <BooleanListFilter<ExistingTagFormType>
            description={intl.formatMessage("settings.tags.filters.active")}
            filterId="TagActive"
            predicate={(item) => item.$.enabled.$}
          />
          <BooleanListFilter<ExistingTagFormType>
            description={intl.formatMessage("settings.tags.filters.required")}
            filterId="TagRequired"
            predicate={(item) => item.$.required.$}
          />
          <BooleanListFilter<ExistingTagFormType>
            description={intl.formatMessage("settings.tags.filters.priority")}
            filterId="TagPriority"
            predicate={(item) => item.$.priority.$}
          />
          <BooleanListFilter<ExistingTagFormType>
            description={intl.formatMessage("settings.tags.filters.showInDetails")}
            filterId="TagDetails"
            predicate={(item) => item.$.showInConversationDetails.$}
          />
        </ListFilterWrapper>
      </FilterWrapperProvider>
    );
  };

  const renderActionSection = () => {
    const areAllSelected = !!filteredItems.length && size(selectedItems) === size(filteredItems);

    return (
      <SettingsForm
        disabled={isEmpty(editedItems)}
        onDiscard={onDiscardAll}
        onSubmit={onSubmitAll}
        displayInline
      >
        <ListActions
          isChecked={areAllSelected}
          onCheckboxChanged={(val) => setItemsSelected(filteredItems, val)}
          isShowingActions={!isEmpty(selectedItems)}
        >
          <VerticalSeparator style="margin-halfLeft" />
          <ListActionsSection>
            <ListBooleanAction
              text={intl.formatMessage("settings.tags.tag.enabled")}
              isActive={forAllSelected((item) => item.$.enabled.$)}
              onClick={(val) => forEachSelected((i) => i.$.enabled.reset(val))}
            />
            <ListBooleanAction
              text={intl.formatMessage("settings.tags.tag.required")}
              isActive={forAllSelected((item) => item.$.required.$)}
              onClick={(val) => forEachSelected((i) => i.$.required.reset(val))}
            />
            <ListBooleanAction
              text={intl.formatMessage("settings.tags.tag.priority")}
              isActive={forAllSelected((item) => item.$.priority.$)}
              onClick={(val) => forEachSelected((i) => i.$.priority.reset(val))}
            />
            <ListBooleanAction
              text={intl.formatMessage("settings.tags.tag.showInDetails")}
              isActive={forAllSelected((item) => item.$.showInConversationDetails.$)}
              onClick={(val) => forEachSelected((i) => i.$.showInConversationDetails.reset(val))}
            />
          </ListActionsSection>
          <VerticalSeparator />
          <ListDeleteAction onDelete={deleteAllSelected} />
        </ListActions>
      </SettingsForm>
    );
  };

  return (
    <>
      {renderNewTagSection()}
      {renderFilterSection()}
      {!isEmpty(filteredItems) && renderActionSection()}
      {renderExistingTagFormTypes()}
    </>
  );
});

type HookParams = Params<ExistingTagFormType, NewTagFormType, TagDto>;
type Hook = ListHook<ExistingTagFormType, NewTagFormType>;
type HookSelectable = ListHookWithSelectables<ExistingTagFormType, NewTagFormType, Hook>;
type HookFilterable = ListHookWithFilters<ExistingTagFormType, NewTagFormType, HookSelectable>;

const useListHook = flow<[HookParams], Hook, HookSelectable, HookFilterable>(
  useSettingsList,
  withSelectables,
  withFilters
);

const useTagsSettingsState = () => {
  const { tagsStore } = useStore();
  const { setLoadingState } = usePreloader();
  const { spinnerService } = useAgentServices();

  const apiCreateItem = (form: NewTagFormType) => {
    return spinnerService.createTag(mapNewFormToCreateForm(form)).then((dto) => {
      tagsStore.addTag(dto);

      return dto;
    });
  };

  const apiGetItems = () => {
    return spinnerService.getTags().then((tags) => {
      tagsStore.setTags(tags);
      return tags;
    });
  };

  const apiDeleteItem = (id: string) => {
    return spinnerService.deleteTag(id).then(() => tagsStore.deleteTag(id));
  };

  const onSubmitCallback = (items: ExistingTagFormType[]) => {
    tagsStore.setTags(items.map(mapFormToTag));
    listHook.deselectAll();
  };

  const onDiscardCallback = () => {
    listHook.deselectAll();
  };

  const listHook = useListHook({
    apiGetItems,
    apiCreateItem,
    apiUpdateItem: (form) =>
      spinnerService.updateTag(form.$.id.$, mapExistingFormToUpdateForm(form)),
    apiDeleteItem,
    mapItemToForm: mapTagToForm,
    copyExistingForm: copyExistingTagForm,
    createNewForm: createNewTagTagForm,
    saveErrorId: "settings.tags.error.wrongTag",
    deleteErrorId: "settings.tags.error.delete",
    onSubmitCallback,
    onDiscardCallback,
    sortBy: (a, b) => +(isSystemTag(a.tag) && !isSystemTag(b.tag)),
    onInitializeResult: setLoadingState
  });

  useEffect(() => {
    listHook.items.forEach((item) => {
      const setEdited = () => listHook.setItemEdited(item);
      item.$.enabled.onUpdate(setEdited);
      item.$.required.onUpdate(setEdited);
      item.$.priority.onUpdate(setEdited);
      item.$.showInConversationDetails.onUpdate(setEdited);
    });
  }, [listHook.items]);

  return listHook;
};
